import { z } from 'zod';
import { ADDRESS_MESSAGES, DEFAULT_COUNTRY, isSupportedCountry, normalizePostalCode, normalizeRegion } from './address';

/** Primary keys are UUID v7 strings. `z.guid()` accepts any RFC-shaped UUID (incl. fixtures). */
export const id = z.guid();
export type Id = z.infer<typeof id>;

/** Money is always stored and transported as integer cents. */
export const cents = z.number().int();
export const nonNegativeCents = z.number().int().nonnegative();

/** Decimal quantities (estimate lines, percentages) travel as numbers, stored as numeric. */
export const quantity = z.number().finite();
export const percent = z.number().min(-100).max(1000);

export const isoDate = z.coerce.date();
/** Calendar date without time, `YYYY-MM-DD`. */
export const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const email = z.email().max(320).transform((v) => v.trim().toLowerCase());
export const phone = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .regex(/^[+()\d\s.\-x]+$/i, 'Invalid phone number');
/** http(s) URLs only: `javascript:`/`data:` URLs would be stored XSS wherever they render as links. */
export const url = z.url({ protocol: /^https?$/ }).max(2048);
export const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected #RRGGBB');
export const slug = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Lowercase letters, numbers and dashes only');

export const nonEmpty = (max = 255) => z.string().trim().min(1).max(max);
export const optionalText = (max = 10_000) => z.string().trim().max(max).optional().nullable();

/**
 * CA <-> US correction: when the filled province/state and postal code are all invalid for the
 * stated country but all valid for the other one (e.g. `ON` + `M5V 2T6` sent as US, or `WA` +
 * `98101` with the country left to default to CA), the address is that other country. Forms that
 * pre-fill a country, and CSV rows without one, then still save correctly; a genuinely mixed
 * address (a US state with a Canadian postal code) is still rejected.
 */
function correctedCountry(country: 'CA' | 'US', region: string, postalCode: string): 'CA' | 'US' {
  if (!region && !postalCode) return country;
  const fits = (c: 'CA' | 'US') => (!region || !!normalizeRegion(c, region)) && (!postalCode || !!normalizePostalCode(c, postalCode));
  const other = country === 'CA' ? 'US' : 'CA';
  return !fits(country) && fits(other) ? other : country;
}

/**
 * A postal address. `country` defaults to Canada. For CA and US addresses the province/state is
 * stored as its two-letter code (a full name is accepted and converted) and the postal code in
 * canonical form (`M5V 2T6`, `98101` / `98101-1234`); invalid values are rejected with a message
 * per country. Empty values stay allowed. Other countries are free-form.
 */
export const addressSchema = z
  .object({
    line1: z.string().trim().max(255).optional().default(''),
    line2: z.string().trim().max(255).optional().default(''),
    city: z.string().trim().max(120).optional().default(''),
    region: z.string().trim().max(120).optional().default(''),
    postalCode: z.string().trim().max(32).optional().default(''),
    country: z
      .string()
      .trim()
      .toUpperCase()
      .length(2, 'Use a two-letter country code, e.g. CA or US')
      .optional()
      .default(DEFAULT_COUNTRY),
    lat: z.number().min(-90).max(90).optional().nullable(),
    lng: z.number().min(-180).max(180).optional().nullable(),
  })
  // Normalize first (keeps the inferred type and the JSON Schema), then validate the result.
  .overwrite((a) => {
    if (!isSupportedCountry(a.country)) return a;
    const country = correctedCountry(a.country, a.region, a.postalCode);
    return {
      ...a,
      country,
      region: a.region ? (normalizeRegion(country, a.region) ?? a.region) : a.region,
      postalCode: a.postalCode ? (normalizePostalCode(country, a.postalCode) ?? a.postalCode) : a.postalCode,
    };
  })
  .superRefine((a, ctx) => {
    if (!isSupportedCountry(a.country)) return;
    const messages = ADDRESS_MESSAGES[a.country];
    if (a.region && normalizeRegion(a.country, a.region) !== a.region) {
      ctx.addIssue({ code: 'custom', path: ['region'], message: messages.region });
    }
    if (a.postalCode && normalizePostalCode(a.country, a.postalCode) !== a.postalCode) {
      ctx.addIssue({ code: 'custom', path: ['postalCode'], message: messages.postalCode });
    }
  });
export type Address = z.infer<typeof addressSchema>;

/** Values of workspace-defined custom fields, stored in the JSONB `custom` column. */
export const customFieldValues = z.record(
  z.string().max(64),
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.string())]),
);
export type CustomFieldValues = z.infer<typeof customFieldValues>;

export const sortDir = z.enum(['asc', 'desc']);

/** Standard list input. Cursor is opaque (the API encodes it). */
export const listInput = z.object({
  cursor: z.string().max(512).optional().nullable(),
  limit: z.number().int().min(1).max(200).default(50),
  search: z.string().trim().max(200).optional(),
  sortBy: z.string().max(64).optional(),
  sortDir: sortDir.default('desc'),
  includeArchived: z.boolean().default(false),
});
export type ListInput = z.infer<typeof listInput>;

export const listOutput = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
export type ListOutput<T> = { items: T[]; nextCursor: string | null };

export const byIdInput = z.object({ id });
export type ByIdInput = z.infer<typeof byIdInput>;

/** Columns shared by every tenant table (see PLAN.md data model). */
export const baseRow = z.object({
  id,
  workspaceId: id,
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: id.nullable(),
  version: z.number().int(),
  archivedAt: z.date().nullable(),
});
export type BaseRow = z.infer<typeof baseRow>;

/** Optimistic concurrency: updates may pass the version they read. */
export const expectedVersion = z.number().int().nonnegative().optional();

type WithoutDefault<S> = S extends z.ZodDefault<infer I> ? I : S;
type UpdateShape<T extends z.ZodRawShape> = { [K in keyof T]: z.ZodOptional<WithoutDefault<T[K]>> };

/**
 * Build an update input: `{ id, expectedVersion?, ...partial(create) }`.
 *
 * Top-level `.default()`s are stripped before making each field optional: zod 4's `.partial()`
 * keeps inner defaults, which would silently reset omitted columns (status, tags, custom...) on
 * every partial update. Omitted fields parse as `undefined` and are left untouched.
 */
export function updateOf<T extends z.ZodRawShape>(create: z.ZodObject<T>) {
  return partialOf(create).extend({ id, expectedVersion });
}

/** Every field optional with its top-level `.default()` stripped, so omitted fields stay omitted. */
export function partialOf<T extends z.ZodRawShape>(schema: z.ZodObject<T>) {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(schema.shape)) {
    const inner = field instanceof z.ZodDefault ? (field.unwrap() as z.ZodType) : (field as unknown as z.ZodType);
    shape[key] = inner.optional();
  }
  return z.object(shape as unknown as UpdateShape<T>);
}

export const fileRef = z.object({
  key: z.string().min(1).max(1024),
  name: z.string().min(1).max(255),
  contentType: z.string().max(255),
  size: z.number().int().nonnegative(),
});
export type FileRef = z.infer<typeof fileRef>;

